import React from 'react';
import { Box, Container, Typography, Stack, Avatar } from '@mui/material';
import { FormatQuote } from '@mui/icons-material';

const colors = {
  cobaltDeep: '#1a2f6c',
  cobaltBright: '#5a7dff',
  cobaltSky: '#7d9fff',
  cobaltIce: '#b3c9ff',
  midnight: '#0f1a34',
  slate: '#293b6d',
  frost: '#f5f7ff',
};

const successStories = [
  {
    company: 'A사 - 플라스틱 사출 업체',
    size: '금형 150개, 직원 30명',
    quote: '처음엔 반신반의했는데, 지금은 없으면 일을 못할 정도입니다. 특히 QR 스캔으로 현장에서 바로 처리하는 게 정말 편해요.',
    name: '김OO 과장',
    role: 'A사 생산팀',
    results: ['금형 찾기 시간 90% 단축', '월 280시간 절약 (840만원)', '금형 고장 50% 감소'],
  },
  {
    company: 'B사 - 금형 제조 업체',
    size: '금형 300개, 직원 50명',
    quote: '고객사마다 언제 어떤 금형으로 얼마나 생산했는지 바로 보여줄 수 있어서 신뢰도가 확 올랐습니다.',
    name: '박OO 이사',
    role: 'B사 영업본부',
    results: ['고객사별 리포트 자동화', '대응 시간 70% 단축', '클레임 80% 감소'],
  },
  {
    company: 'C사 - 자동차 부품 업체',
    size: '금형 80개, 직원 20명',
    quote: '수명 알림 덕분에 미리 점검하니까 갑자기 라인이 멈추는 일이 없어졌어요.',
    name: '이OO 팀장',
    role: 'C사 생산관리팀',
    results: ['라인 정지 제로', '금형 수명 20% 연장', '연간 2,000만원 절감'],
  },
];

const SuccessSection: React.FC = () => {
  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        background: `linear-gradient(165deg, ${colors.midnight} 0%, ${colors.cobaltDeep}20 100%)`,
      }}
    >
      <Container maxWidth="lg">
        <Stack spacing={2} alignItems="center" textAlign="center" mb={8}>
          <Typography
            variant="overline"
            sx={{
              color: colors.cobaltBright,
              fontSize: '0.9rem',
              letterSpacing: '0.15em',
              fontWeight: 600,
            }}
          >
            고객 성공 사례
          </Typography>
          <Typography
            variant="h2"
            sx={{
              color: colors.frost,
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '3rem' },
            }}
          >
            먼저 경험한 고객들의 이야기
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: colors.cobaltIce,
              maxWidth: '700px',
              fontSize: { xs: '1rem', md: '1.1rem' },
              lineHeight: 1.7,
            }}
          >
            EGMMS는 이미 많은 기업의 현장을 바꾸고 있습니다
          </Typography>
        </Stack>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {successStories.map((story, index) => (
            <Box key={index} sx={{ flex: '0 0 auto', width: { xs: '100%', md: 'calc(33.333% - 21.33px)' } }}>
              <Box
                sx={{
                  height: '100%',
                  p: 4,
                  borderRadius: '24px',
                  background: `linear-gradient(135deg, ${colors.slate}50, ${colors.cobaltDeep}20)`,
                  backdropFilter: 'blur(20px)',
                  border: `2px solid ${colors.cobaltBright}30`,
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    borderColor: colors.cobaltBright,
                    boxShadow: `0 20px 40px ${colors.cobaltBright}30`,
                  },
                }}
              >
                <Stack spacing={3}>
                  {/* 인용 아이콘 */}
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: `linear-gradient(135deg, ${colors.cobaltBright}30, ${colors.cobaltSky}20)`,
                    }}
                  >
                    <FormatQuote sx={{ fontSize: 28, color: colors.cobaltBright }} />
                  </Box>

                  {/* 회사 정보 */}
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{ color: colors.frost, fontWeight: 600, mb: 0.5 }}
                    >
                      {story.company}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: colors.cobaltIce, fontSize: '0.9rem' }}
                    >
                      {story.size}
                    </Typography>
                  </Box>

                  {/* 인용구 */}
                  <Typography
                    variant="body1"
                    sx={{
                      color: colors.cobaltIce,
                      fontSize: '1rem',
                      lineHeight: 1.8,
                      fontStyle: 'italic',
                    }}
                  >
                    "{story.quote}"
                  </Typography>

                  {/* 작성자 */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        background: `linear-gradient(135deg, ${colors.cobaltBright}, ${colors.cobaltSky})`,
                        color: colors.frost,
                        fontWeight: 600,
                      }}
                    >
                      {story.name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: colors.frost, fontWeight: 600 }}>
                        {story.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: colors.cobaltIce }}>
                        {story.role}
                      </Typography>
                    </Box>
                  </Box>

                  {/* 결과 */}
                  <Box
                    sx={{
                      pt: 2,
                      borderTop: `1px solid ${colors.cobaltBright}20`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.cobaltSky,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        mb: 1,
                        display: 'block',
                      }}
                    >
                      주요 성과
                    </Typography>
                    <Stack spacing={1}>
                      {story.results.map((result, idx) => (
                        <Typography
                          key={idx}
                          variant="body2"
                          sx={{
                            color: colors.cobaltIce,
                            fontSize: '0.85rem',
                            '&::before': {
                              content: '"✓"',
                              color: colors.cobaltBright,
                              marginRight: '8px',
                              fontWeight: 'bold',
                            },
                          }}
                        >
                          {result}
                        </Typography>
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              </Box>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default SuccessSection;
